/**
 * The palette, once.
 *
 * It used to live twice -- once for the DOM and once in `timeline/theme.ts`, because a canvas
 * cannot read CSS custom properties. That is a real constraint, but it only says the values
 * must reach two places, not that they must be written in two places. So they are written
 * here and flow outward: `theme.ts` imports them directly, and `mantineTheme.ts` hands the
 * same values to Mantine as theme colours.
 *
 * Anything with a name is allowed on screen. Anything without one is a colour somebody typed
 * into a style object at three in the morning: `tokens.test.ts` fails the build if a colour
 * literal (`#hex`, `rgb(`, `oklch(`) reaches any non-test source file but this one, or if a
 * `.css` file reappears under `src`.
 *
 * The hues come from colors.eva.design's five ramps -- primary, success, warning, danger,
 * info -- and the shade taken out of each one was solved rather than picked, against three
 * rules:
 *
 * 1. **Depth is lightness.** The surfaces are one ramp and every one of them is dark. There
 *    is no light card: a light panel that fills the viewport throws tens of times the light
 *    of the page around it, which is glare no amount of choosing a nicer colour fixes.
 * 2. **Hue carries category.** The canvas has to say nine things at once, so the hues are
 *    placed for separation between the ones that are actually drawn next to each other, not
 *    spaced evenly around a wheel and not chosen one at a time by eye.
 * 3. **Everything is ink on something dark.** So every hue lives in a narrow light band and
 *    clears 4.5:1 on all three surfaces -- which is what lets one token do a job that used
 *    to need two.
 *
 * Rules 2 and 3 are why almost nothing here is a `-500`. Eva's mid shades run to C 0.21 in
 * OKLCH, and the chroma ceiling is the brand at C 0.130; its light shades are what clear
 * 4.5:1 on a dark surface anyway. So each role takes the most saturated shade of its ramp
 * that clears both, which is the `-200` or `-300` in every case but the brand's own.
 *
 * Eva has no neutral, violet or pink, so the surfaces, the text, `muted`, `unheard` and
 * `chat` are unchanged. `info` maps to no role here: it is a second blue 22 degrees off the
 * brand, and the two would be drawn in adjacent lanes.
 */

export const token = {
  /**
   * The elevation ramp. Three surfaces, ~0.05 OKLCH lightness apart, faintly warm.
   *
   * Warm because a dead-neutral grey reads as an absence of a decision and a blue-grey reads
   * as every other dark interface; at C 0.005 nobody can name the tint, they can only tell
   * that the greys are not from a screenshot of something else.
   *
   * The step is stated in OKLCH on purpose. WCAG contrast ratios are near-useless between two
   * dark surfaces -- the +0.05 flare term swamps them, and these read as 1.09:1 while being
   * plainly different tones -- so the ramp is checked with the metric that matches the eye.
   */
  bg: "#1b1917",
  /** A panel lifted off the page: the player, the list, the chat, every admin card. */
  bgRaised: "#272523",
  /** Recessed: the timeline well, text inputs, badges. Below the page, not above it. */
  bgSunken: "#110f0d",

  /** Body text. Off-white rather than white: pure white on a dark surface haloes. */
  text: "#f0eeec",
  /** Secondary text. 4.5:1 on the lightest surface it is written on, which is the card. */
  textDim: "#8e8b87",

  /**
   * The brand, and the show blocks on the canvas: Eva `primary-500`.
   *
   * The one shade in the palette that is a `-500`, and the one thing allowed to be the most
   * saturated colour on screen. Its hue is also the middle of the largest empty arc in the
   * wheel below: 177 degrees from `danger`, which shares its lane, and 98 from `jukebox`,
   * which is drawn in the lane directly beneath it.
   */
  accent: "#30bfeb",
  /**
   * Track blocks, and the jukebox chip: Eva `success-200`.
   *
   * Was `accentBlue`, which named the colour instead of the job and then stopped being true.
   * `success-300` and everything below it is over the chroma ceiling.
   */
  jukebox: "#d7f9a5",
  /** A show waiting on a human, the review queue: Eva `warning-200`. */
  warn: "#feeaa5",
  /** Alarm -- the playhead, the live edge, errors: Eva `danger-300`. */
  danger: "#ffaa8b",
  /** Recording indicator in the live duration badge. */
  recording: "#ffa0a0",
  /**
   * The way into the editing UI: Eva `warning-300`, taken orange and to the chroma ceiling.
   *
   * Not `warn`, which it sits 21 degrees from. That one means a show is waiting on a human
   * and is painted by the canvas tooltip, the inspector and the maintenance page; this one
   * means "you can edit here" and is painted in the account menu. They never share a
   * surface, which is the only reason two yellows are allowed at all -- the hue floor in
   * `tokens.test.ts` is about colours drawn against each other, and retuning the review
   * queue's amber should not restyle a button in the header.
   *
   * The one token allowed to sit *on* the ceiling rather than under it, and it is the only
   * ink that earns it: bare on the panel in its off state, with no fill and no border, the
   * colour is the entire control. The pale sand this was reads as disabled there. Orange
   * rather than yellow for the same reason -- at H 81 it was a dimmer `warn`, and the two
   * are only allowed to coexist by being nameable apart.
   */
  admin: "#e9a84e",

  /**
   * Ink on a filled control -- the play button, the LIVE badge, the starred badge.
   *
   * A dark tint of the fill it sits on rather than plain black, and the *lightest* such tint
   * that still clears 4.5:1. Black on a small saturated fill reads as a hole punched in the
   * colour rather than as a letter. Not Eva shades: its dark ends are C 0.14 and up, over
   * the ceiling, so these are its hues taken down in lightness instead.
   */
  onAccent: "#264855",
  onDanger: "#6a4334",
  onStar: "#7a533f",

  /**
   * Kept past the buffer. Also the canvas's starred-track edge: Eva `danger-200`.
   *
   * Warm rather than gold, and that is forced: this is drawn as the edge of a track block,
   * Eva's green is a yellow-green at H 126, and every gold in the palette lands within 40
   * degrees of it. A warm amber is the nearest thing to a gold that can sit on that fill and
   * still be named apart from it.
   */
  star: "#ffccb1",
  /** Tombstones: the archive knows about it, the audio is gone. No Eva neutral to take. */
  muted: "#95918d",
  /**
   * The hatch outside the record: before the archive began, and after the clock. Eva
   * `danger-100`, the palest warm it has.
   */
  gap: "#ffead9",
  /**
   * The chat density lane.
   *
   * Deliberately not the colour of any segment kind: the lane is a different sort of claim --
   * how much was said, not what was on -- and reusing the show or track hue would read as
   * another row of blocks.
   */
  chat: "#e394c2",
  /**
   * Not yet heard, in the three pixels directly above the chat lane.
   *
   * Violet because it is the unvisited-link claim and green would read as done, and no Eva
   * ramp is violet. Those three pixels are why this hue is checked: an older palette had this
   * and `chat` **six degrees apart**, which is one band with a seam in it, not two lanes.
   * They are 56 apart and `tokens.test.ts` holds the floor.
   */
  unheard: "#958cdb",

  white: "#ffffff",
  black: "#000000",
} as const;

export type TokenName = keyof typeof token;

/**
 * Label text drawn on top of a block, tinted toward the block's own hue.
 *
 * Two tiers, because a block is captioned in two: what it is, in the plain tint, and who
 * played it and where it came from beneath, in the dim one. Solved against the block's
 * *composited* fill -- a translucent wash over the well, which is the colour actually under
 * the glyphs -- at 5:1 for the title and 3.4:1 for the line under it. The pair has to read as
 * one caption in two voices, so they are a lightness step at one hue rather than two hues.
 */
export const onBlock = {
  show: "#abd6e6",
  showDim: "#6f98a8",
  provisional: "#aba17e",
  provisionalDim: "#8b815f",
  live: "#bb9587",
  liveDim: "#997568",
  track: "#a9b795",
  trackDim: "#869473",
  /** Neutral, unlike the rest: the token is grey and a warm tint on it reads as a bug. */
  tombstone: "#c5c1bc",
  tombstoneDim: "#8b8783",
} as const;

export const radius = "10px";
/** Controls. Softer than the boxes it replaced, nowhere near a pill. */
export const radiusSm = "6px";

/** `#30bfeb` at 18% -> `rgba(48, 191, 235, 0.18)`. */
export function alpha(hex: string, a: number): string {
  const [r, g, b] = rgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/**
 * The opaque colour an `alpha()` wash composites to over `base`.
 *
 * Canvas alpha interpolates sRGB channel values, so this composites there and not in linear
 * light: it is what the screen actually shows. Two callers want that number rather than the
 * wash -- the contrast audit, which has to judge a caption against the tint under it, and the
 * one fill that cannot be translucent because what it is painted over is not a surface.
 */
export function mix(wash: string, base: string): string {
  const [r, g, b, a] = wash.match(/[\d.]+/g)!.map(Number) as [number, number, number, number];
  const bg = rgb(base);
  return `#${[r, g, b]
    .map((c, i) =>
      Math.round(c * a + bg[i]! * (1 - a))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}

export function rgb(hex: string): [number, number, number] {
  const value = hex.replace("#", "");
  const full =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

/**
 * WCAG 2.1 relative luminance, and the contrast ratio built on it.
 *
 * Here rather than in the test so the numbers the audit enforces are the same ones anyone
 * picking a new colour can call.
 */
export function luminance(hex: string): number {
  const channel = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = rgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrast(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return (x! + 0.05) / (y! + 0.05);
}

/**
 * OKLCH: lightness, chroma, hue.
 *
 * The palette is stated in hex because that is what CSS and canvas take, but every decision
 * in it was made in these three numbers, and hex hides all of them. `#e394c2` and `#958cdb`
 * are both "a mauve" by eye and by name; only the hue says one is 56 degrees from the other,
 * which is the difference between two lanes and one smear.
 */
export function oklch(hex: string): { L: number; C: number; H: number } {
  const toLinear = (v: number) => {
    const s = v / 255;
    return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  const [r, g, b] = rgb(hex).map(toLinear) as [number, number, number];
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  const H = (Math.atan2(B, A) * 180) / Math.PI;
  return {
    L: 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    C: Math.sqrt(A * A + B * B),
    H: H < 0 ? H + 360 : H,
  };
}

/** Smallest angle between two hues, so 350 and 10 are 20 apart rather than 340. */
export function hueGap(a: string, b: string): number {
  const d = Math.abs(oklch(a).H - oklch(b).H) % 360;
  return Math.min(d, 360 - d);
}

/** FNV-1a. Small, fast, and stable across reloads, which is all that is wanted here. */
function hash(text: string): number {
  let value = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    value ^= text.charCodeAt(i);
    value = Math.imul(value, 0x01000193);
  }
  return value >>> 0;
}

/**
 * A flat hashed colour for a cover with no artwork, stated in OKLCH so the hash picks a hue
 * and nothing else.
 *
 * It used to be `hsl(h 45% 30%)`, which has two faults that only show up once the rest of
 * the palette is calm. HSL "lightness" is not perceptual, so the same 30% is a muddy navy at
 * hue 250 and a hot magenta at hue 300 -- white initials over the range ran anywhere from
 * 5.3:1 to 12.7:1, and a column of tiles read as a column of different weights. And at its
 * worst hue it out-saturated the brand, which made a hashed fallback the loudest element on
 * screen.
 *
 * OKLCH fixes both by construction: fixed L and C means every hue lands at one perceived
 * weight and one saturation. That is the whole claim, so `Artwork.test.ts` holds exactly it
 * -- L and C constant across every seed, C inside the ceiling -- and the legibility follows
 * from the fixed L rather than needing its own pin at 360 hues.
 *
 * L and C came down when the page went dark. They were set against a white card, where a
 * tile had to be dark to hold white initials; against a dark card the same tile is the
 * brightest large surface on the page, and it is a *placeholder*. At 0.42 it still reads as
 * a raised block -- 0.154 lightness above the card -- and the initials never drop below 8:1.
 *
 * It lives here and not beside the tile it fills because this file is the only place in the
 * app a colour may be written, and `tokens.test.ts` holds that.
 */
export function colourFor(seed: string): string {
  const h = hash(seed || "untitled");
  return `oklch(0.42 0.06 ${h % 360})`;
}
